<script setup lang="ts">
import { computed, ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  useUxFeedbackStore,
  useTeamStore,
  type TeamDto,
} from '@blueprint/frontend-basics'

import CreationProgress from '@/components/molecules/CreationProgress.vue'
import ShopSuccess from '@/components/molecules/ShopSuccess.vue'
import { CreateShopDto, type ShopDto } from '@/models/dtos/shop.dto.ts'
import { useServiceStore } from '@/stores/service.store.ts'

const props = defineProps<{
  shopName: string
  hasTeams: boolean
  selectedTeam: TeamDto | null
}>()

const emit = defineEmits<{
  completed: [payload: { team: TeamDto; shop: ShopDto }]
  error: [error: unknown]
  'title-change': [title: string]
}>()

const { t } = useI18n()
const serviceStore = useServiceStore()
const teamStore = useTeamStore()
const ux = useUxFeedbackStore()

const currentStep = ref(0)
const success = ref(false)
const createdTeam = ref<TeamDto | null>(null)
const createdShop = ref<ShopDto | null>(null)
const progressTimerId = ref<ReturnType<typeof setInterval> | null>(null)
const teamPhaseCompleted = ref(false)
const visualCompleted = ref(false)
const actionsCompleted = ref(false)
const finalized = ref(false)
const finalizeScheduleId = ref<ReturnType<typeof setTimeout> | null>(null)
const finalStepDwellMs = 800

const steps = computed<string[]>(() => [
  props.hasTeams ? t('home.step2') : t('home.step1'),
  t('home.step2'),
  t('home.step3'),
])
const progressPercentage = computed(
  () => (currentStep.value / steps.value.length) * 100,
)

const startSteadyProgress = (): void => {
  currentStep.value = 1
  const incrementEveryMs = 900
  const maxStep = steps.value.length
  progressTimerId.value = setInterval(() => {
    if (success.value || finalized.value) {
      if (progressTimerId.value) clearInterval(progressTimerId.value)
      progressTimerId.value = null
      return
    }
    if (currentStep.value < maxStep) {
      currentStep.value += 1
      if (currentStep.value >= maxStep) {
        visualCompleted.value = true
        if (progressTimerId.value) clearInterval(progressTimerId.value)
        progressTimerId.value = null
        tryScheduleFinalize()
      }
    }
  }, incrementEveryMs)
}

const stopSteadyProgress = (): void => {
  if (progressTimerId.value) {
    clearInterval(progressTimerId.value)
    progressTimerId.value = null
  }
}

const finalizeSuccess = (): void => {
  if (finalized.value) return
  finalized.value = true
  success.value = true
  currentStep.value = steps.value.length
  stopSteadyProgress()
  ux.queueMessage({
    type: 'success',
    title: t('home.shopCreated'),
    message: t('home.shopCreatedMessage'),
  })
  emit('title-change', t('home.shopCreated'))
  setTimeout(() => {
    emit('completed', {
      team: createdTeam.value as TeamDto,
      shop: createdShop.value as ShopDto,
    })
  }, 1200)
}

const tryScheduleFinalize = (): void => {
  if (
    visualCompleted.value &&
    actionsCompleted.value &&
    !finalized.value &&
    finalizeScheduleId.value === null
  ) {
    finalizeScheduleId.value = setTimeout(() => {
      finalizeScheduleId.value = null
      finalizeSuccess()
    }, finalStepDwellMs)
  }
}

const start = async (): Promise<void> => {
  emit('title-change', t('home.creatingShop'))
  startSteadyProgress()

  try {
    let teamToUse: TeamDto

    if (props.hasTeams && props.selectedTeam) {
      teamToUse = props.selectedTeam
      await teamStore.selectTeam(serviceStore, teamToUse)
      teamPhaseCompleted.value = true
    } else {
      const newTeam = await serviceStore.teamService.createTeam({
        name: props.shopName.trim(),
      })
      if (newTeam.id) {
        teamStore.teams.set(newTeam.id, newTeam)
        await teamStore.selectTeam(serviceStore, newTeam)
      }
      teamToUse = newTeam
      teamPhaseCompleted.value = true
    }

    const createShopDto = new CreateShopDto(
      props.shopName.trim(),
      teamToUse.id!,
    )
    const created = await serviceStore.shopService.createShop(createShopDto)
    if (!created?.id) {
      throw new Error('Shop creation did not return a valid shop')
    }
    createdShop.value = created
    createdTeam.value = teamToUse

    actionsCompleted.value = true
    tryScheduleFinalize()
  } catch (error) {
    stopSteadyProgress()
    console.error('Error creating shop:', error)
    ux.queueMessage({
      type: 'error',
      title: t('general.error'),
      message: t('home.errorCreatingShop'),
    })
    setTimeout(() => emit('error', error), 300)
  }
}

defineExpose({ start })

onUnmounted(() => {
  stopSteadyProgress()
  if (finalizeScheduleId.value) {
    clearTimeout(finalizeScheduleId.value)
    finalizeScheduleId.value = null
  }
})
</script>

<template>
  <div>
    <CreationProgress
      v-if="!success"
      :steps="steps"
      :current-step="currentStep"
      :progress-percentage="progressPercentage"
    />
    <ShopSuccess v-else />
  </div>
</template>
